import type { ReactNode } from "react";
import { Button } from "../../../components/ui/button";
import { Check, Clock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";

// ============================================================================
// MarkAllHeader — bulk status buttons (mobile + desktop grid variants)
// ============================================================================

export interface MarkAllHeaderProps {
  isMobile: boolean;
  setAllStudentsStatus: (
    status: "present" | "late" | "excused" | "unexcused",
  ) => void;
  /**
   * Optional content for the left (name) column of the "Alle" row. Used by
   * the prefill-summary card to label its pending subsection ("N noch
   * nicht erfasst:") right next to the bulk buttons instead of as a
   * separate line above. Empty when the header sits above the real
   * student grid.
   */
  leftLabel?: ReactNode;
}

export function MarkAllHeader({
  isMobile,
  setAllStudentsStatus,
  leftLabel,
}: MarkAllHeaderProps) {
  if (isMobile) {
    // Match the body grid: 3fr name + four equal status columns. Each
    // "Alle" button lands directly above its column. Spät gets extra
    // breathing room because accidental "Alle Spät" taps were common.
    return (
      <div
        className="grid gap-1 pb-2 border-b items-center"
        style={{ gridTemplateColumns: "3fr repeat(4, 1fr)" }}
      >
        <div className="min-w-0">{leftLabel}</div>
        <div className="flex justify-center">
          <Button
            aria-label="Alle anwesend"
            title="Alle anwesend"
            variant="outline"
            size="sm"
            className="text-xs px-0 py-1 h-7 w-9"
            onClick={() => setAllStudentsStatus("present")}
          >
            <Check className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex justify-center px-1">
          <Button
            aria-label="Alle verspätet"
            title="Alle verspätet"
            variant="outline"
            size="sm"
            className="text-xs px-0 py-1 h-7 w-9"
            onClick={() => setAllStudentsStatus("late")}
          >
            <Clock className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex justify-center">
          <Button
            aria-label="Alle informiert"
            title="Alle informiert"
            variant="outline"
            size="sm"
            className="text-xs px-0 py-1 h-7 w-9"
            onClick={() => setAllStudentsStatus("excused")}
          >
            Inf
          </Button>
        </div>
        <div className="flex justify-center">
          <Button
            aria-label="Alle ungeklärt"
            title="Alle ungeklärt"
            variant="outline"
            size="sm"
            className="text-xs px-0 py-1 h-7 w-9"
            onClick={() => setAllStudentsStatus("unexcused")}
          >
            U
          </Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div
        className="grid gap-1 lg:gap-2 text-[11px] text-gray-500 mb-1"
        style={{ gridTemplateColumns: "3fr repeat(4, 1fr)" }}
      >
        <div></div>
        <div className="text-center">Anw.</div>
        <div className="text-center">Versp.</div>
        <div className="text-center">Informiert</div>
        <div className="text-center">Ungeklärt</div>
      </div>
      <div
        className="grid gap-1 lg:gap-2 pb-2 border-b items-center"
        style={{ gridTemplateColumns: "3fr repeat(4, 1fr)" }}
      >
        <div className="min-w-0">{leftLabel}</div>
        <div className="flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Alle anwesend"
                variant="outline"
                size="sm"
                className="text-xs px-1 lg:px-2 py-1 h-6 lg:h-7"
                onClick={() => setAllStudentsStatus("present")}
              >
                Alle
              </Button>
            </TooltipTrigger>
            <TooltipContent>Alle anwesend</TooltipContent>
          </Tooltip>
        </div>
        <div className="flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Alle verspätet"
                variant="outline"
                size="sm"
                className="text-xs px-2 py-1 h-7"
                onClick={() => setAllStudentsStatus("late")}
              >
                Alle
              </Button>
            </TooltipTrigger>
            <TooltipContent>Alle verspätet</TooltipContent>
          </Tooltip>
        </div>
        <div className="flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Alle entschuldigt"
                variant="outline"
                size="sm"
                className="text-xs px-2 py-1 h-7"
                onClick={() => setAllStudentsStatus("excused")}
              >
                Alle
              </Button>
            </TooltipTrigger>
            <TooltipContent>Alle entschuldigt</TooltipContent>
          </Tooltip>
        </div>
        <div className="flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Alle unentschuldigt"
                variant="outline"
                size="sm"
                className="text-xs px-2 py-1 h-7"
                onClick={() => setAllStudentsStatus("unexcused")}
              >
                Alle
              </Button>
            </TooltipTrigger>
            <TooltipContent>Alle unentschuldigt</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}
